import type { Appointment } from "@/features/appointments/types/appointmentTypes";
import { AppointmentStatusBadge } from "@/features/appointments/components/AppointmentStatusBadge";
import { useClient } from "@/features/clients/hooks/clientQueries";
import { useServices } from "@/features/services/hooks/serviceQueries";
import { getServiceName } from "@/features/services/utils/getServiceName";
import { Button } from "@/shared/components/shadcn/Button";
import { Card } from "@/shared/components/ui/Card";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { formatBusinessDate, formatMoney, getBusinessDateKey } from "@/shared/utils/formatters";

interface Props {
  appointment?: Appointment;
  now: Date;
  timeZone: string;
  currency: string;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
}

function NextAppointment({ appointment, now, timeZone, currency }: Required<Pick<Props, "appointment" | "now" | "timeZone" | "currency">>) {
  const client = useClient(appointment.clientId || "");
  const services = useServices();
  const { goToAppointmentDetails } = useAppNavigation();
  const start = appointment.startAt!;
  const minutes = Math.ceil((start.getTime() - now.getTime()) / 60_000);
  const sameDay = getBusinessDateKey(start, timeZone) === getBusinessDateKey(now, timeZone);
  const bookedDurations = appointment.services.map((service) => service.durationMinutes);
  const duration = bookedDurations.length && bookedDurations.every((value) => value !== null && Number.isFinite(value) && value > 0)
    ? bookedDurations.reduce<number>((total, value) => total + (value ?? 0), 0)
    : appointment.endAt ? Math.max(0, Math.round((appointment.endAt.getTime() - start.getTime()) / 60_000)) : null;
  const error = client.error || services.error;

  return (
    <>
      <p className="mb-3 text-xs font-bold uppercase tracking-widest">
        Coming up{minutes <= 0 ? appointment.status === "pending" ? " · Awaiting confirmation" : " · Scheduled now" : sameDay && minutes < 60 ? ` · In ${minutes} min` : ""}
      </p>
      {error ? <ErrorState message={error.message} onRetry={() => { void client.refetch(); void services.refetch(); }} />
        : (client.isLoading || services.isLoading) ? <RowSkeleton /> : (
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center sm:gap-x-6">
            <div className="min-w-0">
              <h2 className="text-2xl font-extrabold leading-tight [overflow-wrap:anywhere]">
                {client.data ? `${client.data.firstName} ${client.data.lastName}`.trim() : appointment.clientId ? "Customer unavailable" : "No customer assigned"}
              </h2>
              <p className="mt-1 text-sm leading-relaxed">{getServiceName(appointment, services.data) || "No service assigned"}{duration !== null && ` · ${duration} min`}</p>
              {appointment.price !== null && <p className="mt-1 text-xs">{formatMoney(appointment.price, appointment.billingCurrency ?? currency)} booked value</p>}
            </div>
            <div className="flex flex-col items-end gap-2">
              {!sameDay && <span className="text-xs">{formatBusinessDate(start, timeZone, { weekday: "short", month: "short", day: "numeric" })}</span>}
              <time dateTime={start.toISOString()} className="text-3xl font-extrabold tabular-nums">{formatBusinessDate(start, timeZone, { hour: "2-digit", minute: "2-digit" })}</time>
              <AppointmentStatusBadge status={appointment.status} />
            </div>
            <Button variant="outline" className="col-span-2 mt-2 w-full sm:col-span-1 sm:mt-0 sm:w-auto" onClick={() => goToAppointmentDetails(appointment.id, false)}>View appointment</Button>
          </div>
        )}
    </>
  );
}

export function DashboardUpcomingMeetingsSummary({ appointment, loading, error, onRetry, ...props }: Props) {
  return (
    <Card tone="matcha" role="region" aria-label="Coming up">
      {error ? <><h2 className="mb-3 font-bold">Coming up</h2><ErrorState message={error.message} onRetry={onRetry} /></>
        : loading ? <><h2 className="mb-3 font-bold">Coming up</h2><RowSkeleton /></>
        : appointment ? <NextAppointment appointment={appointment} {...props} />
        : <><h2 className="font-bold">Coming up</h2><EmptyState message="No upcoming appointments at this location." /></>}
    </Card>
  );
}
